"use client"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

// 功能卡片数据
const featureCards = [
  {
    title: "ADME/T Druggability",
    description: "Drug metabolism and toxicity prediction to optimize pharmacokinetic properties of compounds. Analyze absorption, distribution, metabolism, excretion, and toxicity profiles to support lead optimization and candidate selection.",
    href: "/login",
    available: true,
    color: "from-blue-500 to-cyan-400"
  },
  {
    title: "Affinity Prediction",
    description: "Predict binding affinity between small molecules and protein targets to accelerate drug discovery. KINET platform employs multimodal deep learning architectures that integrate structural information and biophysical features to precisely evaluate binding conformations and affinity coefficients for novel compounds.",
    href: "/login",
    available: false,
    color: "from-purple-500 to-indigo-400"
  },
  {
    title: "Molecular Generation",
    description: "AI-based molecular design and generation to create novel compounds with specific properties. KINET platform employs generative models to explore chemical space and propose structures with optimal target activity and drug-like properties.",
    href: "/login",
    available: false,
    color: "from-emerald-500 to-teal-400"
  },
  {
    title: "LexFold",
    description: "Complex structure prediction and analysis to provide structural basis for targeted drug design. Utilize our advanced protein folding algorithms to model protein structures and identify potential binding sites for rational drug design.",
    href: "/login",
    available: false,
    color: "from-amber-500 to-yellow-400"
  }
]

export default function Home() {
  return (
    // 外层容器
    <div className="w-full h-screen bg-gray-900 flex flex-col">
      {/* 可滚动区域 */}
      <div className="flex-grow overflow-y-auto custom-scrollbar">
        {/* 背景图片带叠加渐变效果 */}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-indigo-900/70 to-purple-900/70"></div>
          <img
            src="/images/login-bg.jpg"
            alt="Background"
            className="h-full w-full object-cover opacity-60 mix-blend-overlay"
          />
          {/* 科技感网格元素 */}
          <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center opacity-20"></div>
          {/* 光效 */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[128px] opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[128px] opacity-20"></div>
        </div>
        
        {/* Logo */}
        <div className="sticky top-8 left-8 z-10 ml-8 mt-8">
          <img
            src="/images/logo.png"
            alt="Company Logo"
            width={120}
            height={40}
            className="drop-shadow-lg"
          />
        </div>

        <div className="flex flex-col items-center px-4 py-16 relative z-10">
          {/* 标题区域 */}
          <div className="w-full max-w-4xl text-center mb-16">
            <h1 className="text-5xl font-bold text-white mb-4 tracking-tight drop-shadow-md flex justify-center leading-tight">
              <div className="flex items-baseline">
                <img src="/images/kinet_logo.png" alt="KINET" className="h-8 mr-2" />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 leading-normal">AI Drug Design Platform</span>
              </div>
            </h1>
            <p className="text-xl text-white/90 mb-8">Integrating advanced artificial intelligence technology to accelerate drug development</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link href="/login">
                <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-2 text-lg shadow-lg shadow-blue-500/30 hover:shadow-blue-600/40 border-0 transition-all duration-300">
                  Login
                </Button>
              </Link>
              <Link href="/register">
                <Button className="bg-transparent border-2 border-white hover:bg-white/10 text-white px-8 py-2 text-lg shadow-lg transition-all duration-300">
                  Register
                </Button>
              </Link>
            </div>
          </div>

          {/* 功能卡片区 */}
          <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {featureCards.map((card, index) => (
              <Card key={index} className="bg-white backdrop-blur-sm hover:bg-white/95 transition-all flex flex-col h-full border-0 shadow-xl overflow-hidden">
                <div className={`h-1 w-full bg-gradient-to-r ${card.color}`}></div>
                <CardHeader className="pb-2 pt-5 px-6">
                  <div className="flex justify-between items-start h-16">
                    <CardTitle className="text-xl font-bold">{card.title}</CardTitle>
                    {!card.available && (
                      <div className="px-2 py-0.5 text-[10px] whitespace-nowrap rounded-md bg-amber-50 text-amber-800 border border-amber-200 font-medium leading-none">
                        Coming Soon
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-grow px-6 pb-0">
                  <CardDescription className="text-gray-700 text-sm leading-relaxed">
                    {card.description}
                  </CardDescription>
                </CardContent>
                <div className="p-6 mt-auto flex justify-center">
                  {card.available ? (
                    <Button variant="outline" className="w-[140px] rounded-md border-gray-300 hover:bg-gradient-to-r hover:border-transparent hover:text-white transition-all duration-300" 
                      style={{
                        '--tw-gradient-from': card.color.split(' ')[0].replace('from-', ''),
                        '--tw-gradient-to': card.color.split(' ')[1].replace('to-', '')
                      } as React.CSSProperties}>
                      <Link href={card.href} className="w-full h-full flex items-center justify-center">
                        Learn More
                      </Link>
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-[140px] rounded-md border-gray-300 text-gray-400 cursor-not-allowed" disabled>
                      Learn More
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* 技术优势 */}
          <div className="w-full max-w-4xl bg-white backdrop-blur-sm p-8 rounded-xl shadow-2xl border-0 mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Our Technical <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">Advantages</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 flex items-center justify-center text-white mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z"></path><path d="M7 7h.01"></path></svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Advanced AI Algorithms</h3>
                </div>
                <p className="text-gray-600 ml-[52px]">Leveraging advanced deep learning and molecular simulation technologies to deliver accurate support for drug design</p>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-indigo-400 flex items-center justify-center text-white mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h20"></path><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"></path><path d="m7 21 5-5 5 5"></path></svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Comprehensive Innovation</h3>
                </div>
                <p className="text-gray-600 ml-[52px]">Guiding the entire journey of drug discovery, from molecular design to ADME/T prediction</p>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 flex items-center justify-center text-white mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4"></path><path d="M12 18v4"></path><path d="M4.93 4.93l2.83 2.83"></path><path d="M16.24 16.24l2.83 2.83"></path><path d="M2 12h4"></path><path d="M18 12h4"></path><path d="M4.93 19.07l2.83-2.83"></path><path d="M16.24 7.76l2.83-2.83"></path></svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">User-Friendly Interface</h3>
                </div>
                <p className="text-gray-600 ml-[52px]">An intuitive workflow that transforms complex process into smooth efficiency</p>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 flex items-center justify-center text-white mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.17 14.83l1.7 1.7c.2.2.5.2.7 0L16 12"></path><path d="M5 12a7 7 0 1 0 14 0 7 7 0 0 0-14 0Z"></path></svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Demonstrated Validation</h3>
                </div>
                <p className="text-gray-600 ml-[52px]">Rigorously tested models providing consistently reliable predictions with high accuracy across diverse chemical space</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.1);
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.5);
          border-radius: 2px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.7);
        }

        @supports (scrollbar-width: thin) {
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(59, 130, 246, 0.5) transparent;
          }
        }
      `}</style>
    </div>
  )
}